import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Pagination,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import PaidIcon from "@mui/icons-material/Paid";
import CasinoIcon from "@mui/icons-material/Casino";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { registrationDateTime } from "../utils/registrationDateTime";
import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import ResponsiveDialog from "../components/ResponsiveDialog";
import {
  adminPageSx,
  contentCardSx,
  filterStackSx,
  metricCardSx,
  metricGridSx,
  mobileActionGroupSx,
  paginationBarSx,
  dialogActionsResponsiveSx
} from "../utils/adminResponsive";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedUserForEmail, setSelectedUserForEmail] = useState(null);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: ""
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const rowsPerPage = 5;
  const navigate = useNavigate();
  const admin = useSelector((state) => state.auth.admin);
  const isSuperAdmin = admin === "super_admin";

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const loadUsers = async () => {
    setLoading(true);

    try {
      const data = await adminFetch("/api/user/all/super-admin", {
        method: "GET"
      });

      const dashboardData = await adminFetch("/api/admin/dashboard", {
        method: "GET"
      });

      if (data.status) {
        setUsers(data.users.rows || []);
      }

      if (dashboardData?.status) {
        setStats(dashboardData.data?.stats || {});
      }
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const filteredUsers = users.filter((user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const matchesSearch =
      fullName.includes(search.toLowerCase()) ||
      String(user.email || "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || user.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  useEffect(() => {
    if (page > Math.max(totalPages, 1)) {
      setPage(1);
    }
  }, [page, totalPages]);

  const analyticsCards = [
    {
      label: "Total Users",
      value: Number(stats.totalUsers || 0).toLocaleString(),
      icon: PeopleIcon
    },
    {
      label: "Tickets Sold",
      value: Number(stats.ticketsSold || 0).toLocaleString(),
      icon: ConfirmationNumberIcon
    },
    {
      label: "Eligible Tickets",
      value: Number(stats.eligibleTickets || 0).toLocaleString(),
      icon: ConfirmationNumberIcon
    },
    {
      label: "Revenue",
      value: `₦${Number(stats.revenue || 0).toLocaleString()}`,
      icon: PaidIcon
    },
    {
      label: "Total Draws",
      value: Number(stats.totalDraws || 0).toLocaleString(),
      icon: CasinoIcon
    },
    {
      label: "Total Admins",
      value: Number(stats.totalAdmins || 0).toLocaleString(),
      icon: AdminPanelSettingsIcon
    }
  ];

  const openEmailDialog = (user) => {
    setSelectedUserForEmail(user);
    setEmailForm({
      subject: "",
      message: ""
    });
    setEmailDialogOpen(true);
  };

const closeEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedUserForEmail(null);
    setEmailForm({
      subject: "",
      message: ""
    });
  };

  const handleSendDirectEmail = async () => {
    if (!selectedUserForEmail?.id) {
      showSnackbar("Select a user first", "error");
      return;
    }

    const subject = emailForm.subject.trim();
    const message = emailForm.message.trim();

    if (!subject || !message) {
      showSnackbar("Subject and message are required", "error");
      return;
    }

    setSendingEmail(true);

    try {
      const data = await adminFetch(`/api/user/${selectedUserForEmail.id}/send-email`, {
        method: "POST",
        body: JSON.stringify({
          subject,
          message
        })
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to send email", "error");
        setSendingEmail(false);
        return;
      }

      showSnackbar(data?.message || "Email sent successfully");
      setSendingEmail(false);
      closeEmailDialog();
      return;
    } catch (err) {
      console.log(err);
      showSnackbar("Failed to send email", "error");
    }

    setSendingEmail(false);
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "active":
        return <Chip label="Active" sx={{ bgcolor: "#2e7d32", color: "#fff" }} />;

      case "suspended":
        return <Chip label="Suspended" sx={{ bgcolor: "#ed6c02", color: "#fff" }} />;

      case "closed":
        return <Chip label="Closed" sx={{ bgcolor: "#d32f2f", color: "#fff" }} />;

      default:
        return <Chip label={status} />;
    }
  };

  const userTableColumns = [
    {
      key: "index",
      label: "S/N",
      render: (_user, index) => (page - 1) * rowsPerPage + index + 1,
      hideOnMobile: true
    },
    {
      key: "profile_id",
      label: "Profile ID",
      render: (user) => user.profile_id || "--"
    },
    {
      key: "name",
      label: "Profile Name",
      render: (user) => user.profile_name || [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" "),
      hideOnMobile: true
    },
    {
      key: "email",
      label: "Email",
      render: (user) => user.email
    },
    {
      key: "phone",
      label: "Phone",
      render: (user) => user.phone || "-"
    },
    {
      key: "status",
      label: "Status",
      render: (user) => getStatusChip(user.status)
    },
    {
      key: "verified",
      label: "Verified",
      render: (user) => (user.is_verified ? "Yes" : "No")
    },
    {
      key: "created_at",
      label: "Created",
      desktopCellSx: { minWidth: 116 },
      render: (user) => {
        const created = registrationDateTime(user.created_at);
        return <Box sx={{ whiteSpace: "nowrap" }}>
          <Typography component="div" variant="body2">{created.date}</Typography>
          <Typography component="div" variant="caption" color="text.secondary">{created.time}</Typography>
        </Box>;
      }
    },
    {
      key: "actions",
      label: "Actions",
      align: "center",
      isActions: true,
      desktopCellSx: { minWidth: 320 },
      mobileValueSx: mobileActionGroupSx,
      render: (user) => (
        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
          {isSuperAdmin && <Button size="small" variant="contained" sx={{ bgcolor: "#1976d2" }} onClick={() => navigate(`/admin/users/${user.id}/profile`)}>VIEW PROFILE</Button>}
          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: "#1976d2" }}
            onClick={() => navigate("/admin/user/modify", { state: { user } })}
          >
            MODIFY
          </Button>

          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: "#6a1b9a" }}
            onClick={() => openEmailDialog(user)}
          >
            EMAIL
          </Button>

          <Button
            size="small"
            variant="contained"
            sx={{ bgcolor: "#ed6c02" }}
            onClick={() => navigate("/admin/user/suspend", { state: { user } })}
          >
            {user.status !== "suspended" ? "SUSPEND" : "UNSUSPEND"}
          </Button>

          <Tooltip title={!isSuperAdmin ? "Only super admin can delete users" : ""}>
            <span>
              <Button
                size="small"
                variant="contained"
                sx={{
                  bgcolor: "#d32f2f",
                  opacity: isSuperAdmin ? 1 : 0.4
                }}
                disabled={!isSuperAdmin}
                onClick={() => {
                  if (!isSuperAdmin) return;
                  navigate("/admin/user/delete", { state: { user } });
                }}
              >
                DELETE
              </Button>
            </span>
          </Tooltip>
        </Stack>
      )
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 3
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent }}>
          Users Management
        </Typography>

        <Button
          variant="contained"
          sx={{
            backgroundColor: Colors.accent,
            color: "#003300",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" }
          }}
          fullWidth={false}
          onClick={() => navigate("/admin/user/createuser")}
        >
          + Create User
        </Button>
      </Box>

      <Box sx={{ ...metricGridSx, mb: 3 }}>
        {analyticsCards.map((card) => (
          <Card sx={metricCardSx} key={card.label}>
            <card.icon sx={{ fontSize: 40, color: Colors.accent }} />
            <Typography variant="body2">{card.label}</Typography>
            <Typography variant="h6" fontWeight={700}>
              {card.value}
            </Typography>
          </Card>
        ))}
      </Box>

      <Box sx={{ ...filterStackSx, mb: 2 }}>
        <TextField
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          sx={{ background: "white", borderRadius: 1 }}
        />

        <TextField
          select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          size="small"
          fullWidth
          sx={{ background: "white", borderRadius: 1 }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="suspended">Suspended</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
        </TextField>
      </Box>

      <Card
        sx={{
          p: 2,
          ...contentCardSx
        }}
      >
        <Box sx={{ position: "relative", minHeight: 380 }}>
          <ResponsiveDataTable
            columns={userTableColumns}
            rows={paginatedUsers}
            loading={loading}
            minWidth={920}
            emptyMinHeight={300}
            emptyMessage="No users found"
            mobileTitle={(user) => user.profile_name || [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ")}
          />

          <TableLoadingOverlay loading={loading} />
        </Box>

        {!loading && filteredUsers.length > 0 ? (
          <Stack sx={paginationBarSx}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Page {page} of {totalPages || 1}
            </Typography>

            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={(_event, value) => setPage(value)}
              color="primary"
              shape="rounded"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Stack>
        ) : null}
      </Card>

      <ResponsiveDialog open={emailDialogOpen} onClose={closeEmailDialog} maxWidth="sm">
        <DialogTitle>Email Customer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Customer"
              value={
                selectedUserForEmail
                  ? `${selectedUserForEmail.first_name || ""} ${selectedUserForEmail.last_name || ""}`.trim() ||
                    selectedUserForEmail.email
                  : ""
              }
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Email"
              value={selectedUserForEmail?.email || ""}
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Subject"
              value={emailForm.subject}
              onChange={(e) =>
                setEmailForm((prev) => ({
                  ...prev,
                  subject: e.target.value
                }))
              }
              fullWidth
            />

            <TextField
              label="Message"
              value={emailForm.message}
              onChange={(e) =>
                setEmailForm((prev) => ({
                  ...prev,
                  message: e.target.value
                }))
              }
              fullWidth
              multiline
              minRows={6}
              placeholder="Type your message to the customer"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={dialogActionsResponsiveSx}>
          <Button onClick={closeEmailDialog} disabled={sendingEmail}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSendDirectEmail} disabled={sendingEmail}>
            {sendingEmail ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Send Email"
            )}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
